import { ExportContentConfig } from '../types/models';

// =============================================================================
// CONSTANTS & CONFIGURATION
// =============================================================================

export const PDF_IMAGE_MAX_HEIGHT = 500;

// Options to control the TTS pipeline
export interface TTSConfig {
    stripLLMMarkup: boolean;
    unwrapMarkdownBlocks: boolean;
    fixMalformedMarkdown: boolean;
    decodeHTMLEntities: boolean;
    formatLinksAndEmails: boolean;
    formatNumbers: boolean;
    formatCurrency: boolean;
    formatVersionNumbers: boolean;
    formatTemperatureUnits: boolean;
    formatDistanceUnits: boolean;
    cleanupArtifacts: boolean;
}

export const DEFAULT_CONTENT_CONFIG: ExportContentConfig = {
    includeMarkdown: true,
    includeCitations: true,
    includeReasoning: true,
    includeMetadata: true,
    includeTimestamp: false,
    showModel: true,
    showTitle: true,
    showRoleHeader: true,
    includeImages: true,
    embedImages: true,
    imageQuality: 100,
};

export const DEFAULT_TTS_CONFIG: TTSConfig = {
    stripLLMMarkup: true,
    unwrapMarkdownBlocks: true,
    fixMalformedMarkdown: true,
    decodeHTMLEntities: true,
    formatLinksAndEmails: true,
    formatNumbers: true,
    formatCurrency: true,
    formatVersionNumbers: true,
    formatTemperatureUnits: true,
    formatDistanceUnits: true,
    cleanupArtifacts: true,
};

export const DEFAULT_PROCESSING_CONFIG = {
    content: DEFAULT_CONTENT_CONFIG,
    tts: DEFAULT_TTS_CONFIG,
};

export const REGEX = {
    // Markdown structure patterns
    // Bounded quantifiers prevent polynomial backtracking on adversarial input.
    ENTIRELY_CODE_BLOCK: /^\s*```(\w{0,32})\n?([\s\S]{0,1000000}?)\n?```\s*$/,
    MARKDOWN_CODE_BLOCK: /(?:markdown\s*)?```markdown\s*\n?([\s\S]{0,1000000}?)\n?```/g,
    UNCLOSED_LINK: /(!?\[.{0,1000}?])\([^)]{0,2000}$/,

    // Cleanup patterns
    LIST_WRAPPERS: /<\/?(u|o)l>/g,
    LEFTOVER_MARKDOWN: /[*_~`#[\]]/g,
    STRAY_IMAGE_BANG: /!\s*\[/g,
    EXTRA_WHITESPACE: /\s+/g,
    TRAILING_PUNCTUATION: /[.\s]*$/,

    // LLM-specific patterns
    LLM_THINKING_TAGS: /<thinking>[\s\S]{0,1000000}?<\/thinking>/g,
    LLM_ROLE_TAGS: /<\|im_start\|>|<\|im_end\|>/g,

    // Number formatting patterns
    // Matches numbers that should be read digit-by-digit (e.g., IDs, phone numbers, long numeric codes).
    ID_LIKE_NUMBER:
        /\b((?:\w+-){2,}\w+|\d{3,}-[\w-]+|(?<!-)\d{5,}(?!-))\b|\(\d{3,}\)/g,
    NUMBER_COMMAS: /(?<=\d),(?=\d)/g, // Matches commas between digits for removal.
    VERSION_NUMBERS: /(v)(\d+(\.\d+)*)/gi, // Matches version numbers like 'v1.2.3'.

    // Temperature patterns
    // Bounded quantifiers prevent polynomial backtracking on long digit runs.
    TEMPERATURE_CELSIUS: /(-?\d{1,15}(?:\.\d{1,15})?)\s*°?C\b/g,
    TEMPERATURE_FAHRENHEIT: /(-?\d{1,15}(?:\.\d{1,15})?)\s*°?F\b/g,

    // Distance patterns
    DISTANCE_KILOMETERS: /(\d{1,15}(?:\.\d{1,15})?)\s*km\b/gi,
    DISTANCE_MILES: /(\d{1,15}(?:\.\d{1,15})?)\s*mi\b/gi,
    DISTANCE_METERS: /(\d{1,15}(?:\.\d{1,15})?)\s*m\b/gi,
};

// =============================================================================
// TEXT PROCESSING PIPELINE
// =============================================================================

// --- Pre-processing Functions ---

// Strips LLM-specific markup that interferes with markdown parsing
function stripLLMMarkup(text: string): string {
    return text
        .replace(REGEX.LLM_THINKING_TAGS, ' ')
        .replace(REGEX.LLM_ROLE_TAGS, '');
}

// Unwraps markdown code blocks when appropriate
function unwrapMarkdownBlocks(text: string): string {
    let result = text;
    // Check if the entire message is a code block
    const match = result.match(REGEX.ENTIRELY_CODE_BLOCK);
    if (match) {
        const language = match[1];
        const code = match[2];

        // Only unwrap if the language is explicitly 'markdown'
        if (language.toLowerCase() === 'markdown') {
            result = code;
        }
    }

    // Handle embedded markdown blocks
    return result.replace(REGEX.MARKDOWN_CODE_BLOCK, '$1');
}

// Fixes malformed markdown that the parser doesn't handle well
function fixMalformedMarkdown(text: string): string {
    return text.replace(REGEX.UNCLOSED_LINK, '$1');
}

// Orchestrates all pre-processing steps based on configuration
export function preProcessMarkdown(text: string, config: TTSConfig): string {
    let result = text;

    if (config.stripLLMMarkup) {
        result = stripLLMMarkup(result);
    }

    if (config.unwrapMarkdownBlocks) {
        result = unwrapMarkdownBlocks(result);
    }

    if (config.fixMalformedMarkdown) {
        result = fixMalformedMarkdown(result);
    }

    return result;
}

// --- Post-processing Functions ---

/**
 * Utility class for consistent HTML entity processing across the pipeline.
 * Provides centralized entity encoding/decoding to eliminate redundancy.
 */
export class EntityProcessor {
    /**
     * Decodes common HTML entities into their character equivalents.
     * Used to clean content before markdown parsing and rendering.
     */
    static decode(text: string): string {
        return text
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');
    }
}

// Decodes common HTML entities into their character equivalents
function decodeHTMLEntities(text: string): string {
    return EntityProcessor.decode(text);
}

// Formats URLs and email addresses into a more speakable format
function formatLinksAndEmails(text: string): string {
    const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/gi;
    const emailRegex = /([\w.\-+]+)@([\w-]+\.[\w-]+)/g;

    return text
        .replace(urlRegex, (match) => {
            try {
                const url = new URL(match);
                return `link to ${url.hostname.replace(/\./g, ' dot ')}`;
            } catch (e) {
                return 'a malformed link';
            }
        })
        .replace(emailRegex, (_match, user, domain) => {
            const sanitizedDomain = domain.replace(/\./g, ' dot ');
            return `${user} at ${sanitizedDomain}`;
        });
}

// Formats numbers that should be read as individual digits (e.g., IDs, phone numbers)
function formatNumbers(text: string): string {
    return text.replace(REGEX.ID_LIKE_NUMBER, (match) => {
        // Handle parenthesized numbers like (555) separately
        if (match.startsWith('(') && match.endsWith(')')) {
            const numberPart = match.substring(1, match.length - 1);
            return `(${numberPart.split('').join(' ')})`;
        }
        return match.split('').join(' ');
    });
}

// Replaces common currency symbols with their speakable text equivalent
function formatCurrency(text: string): string {
    return text
        .replace(/\$([\d,]+(?:\.\d{1,2})?)/g, '$1 dollars')
        .replace(/€([\d,]+(?:\.\d{1,2})?)/g, '$1 euros');
}

// Formats version numbers to be more speakable
function formatVersionNumbers(text: string): string {
    return text.replace(REGEX.VERSION_NUMBERS, (_match, v, number) => {
        const spokenNumber = number.replace(/\./g, ' point ');
        return `${v} ${spokenNumber}`;
    });
}

// Helper to format numbers with decimals for TTS by spelling out the decimal part.
const speakableDecimal = (number: string): string => {
    if (!number.includes('.')) {
        return number;
    }
    const parts = number.split('.');
    // Handles cases like "1.2.3" by treating them like version numbers.
    if (parts.length > 2) {
        return number.replace(/\./g, ' point ');
    }
    const decimalPart = parts[1].split('').join(' ');
    return `${parts[0]} point ${decimalPart}`;
};

// Formats temperature units to be more speakable
function formatTemperatureUnits(text: string): string {
    return text
        .replace(
            REGEX.TEMPERATURE_CELSIUS,
            (_match, number) => `${speakableDecimal(number)} degrees Celsius`,
        )
        .replace(
            REGEX.TEMPERATURE_FAHRENHEIT,
            (_match, number) =>
                `${speakableDecimal(number)} degrees Fahrenheit`,
        );
}

// Formats distance units to be more speakable
function formatDistanceUnits(text: string): string {
    return text
        .replace(
            REGEX.DISTANCE_KILOMETERS,
            (_match, number) => `${speakableDecimal(number)} kilometers`,
        )
        .replace(
            REGEX.DISTANCE_MILES,
            (_match, number) => `${speakableDecimal(number)} miles`,
        )
        .replace(
            REGEX.DISTANCE_METERS,
            (_match, number) => `${speakableDecimal(number)} meters`,
        );
}

// Removes leftover markdown artifacts and normalizes whitespace
function cleanupArtifacts(text: string): string {
    return text
        .replace(REGEX.STRAY_IMAGE_BANG, '[')
        .replace(REGEX.LIST_WRAPPERS, '')
        .replace(REGEX.LEFTOVER_MARKDOWN, '')
        .replace(REGEX.NUMBER_COMMAS, '')
        .replace(REGEX.EXTRA_WHITESPACE, ' ')
        .trim();
}

// Orchestrates all post-processing steps based on configuration
export function postProcessText(text: string, config: TTSConfig): string {
    let result = text;

    if (config.decodeHTMLEntities) {
        result = decodeHTMLEntities(result);
    }

    if (config.formatLinksAndEmails) {
        result = formatLinksAndEmails(result);
    }

    if (config.formatNumbers) {
        result = formatNumbers(result);
    }

    if (config.formatCurrency) {
        result = formatCurrency(result);
    }

    if (config.formatVersionNumbers) {
        result = formatVersionNumbers(result);
    }

    if (config.formatTemperatureUnits) {
        result = formatTemperatureUnits(result);
    }

    if (config.formatDistanceUnits) {
        result = formatDistanceUnits(result);
    }

    if (config.cleanupArtifacts) {
        result = cleanupArtifacts(result);
    }

    return result;
}
